import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const LANGUAGES = [
  { name: 'Python', icon: 'py' },
  { name: 'Java', icon: 'java' },
  { name: 'C++', icon: 'cpp' },
  { name: 'JavaScript', icon: 'js' },
  { name: 'Go', icon: 'go' },
  { name: 'Rust', icon: 'rs' },
  { name: 'C', icon: 'c' },
];

const DUAL_PAIRS = [
  { left: 'Python', right: 'Java', label: 'Python / Java' },
  { left: 'C++', right: 'Java', label: 'C++ / Java' },
  { left: 'Python', right: 'Go', label: 'Python / Go' },
  { left: 'JavaScript', right: 'Rust', label: 'JS / Rust' },
];

const DEMO_CODE: Record<string, { code: string; output: string }> = {
  Python: {
    code: `def fibonacci(n):
    a, b = 0, 1
    for _ in range(n):
        print(a, end=" ")
        a, b = b, a + b

fibonacci(10)`,
    output: '0 1 1 2 3 5 8 13 21 34',
  },
  Java: {
    code: `public class Main {
    public static void main(String[] args) {
        int a = 0, b = 1;
        for (int i = 0; i < 10; i++) {
            System.out.print(a + " ");
            int temp = a + b;
            a = b; b = temp;
        }
    }
}`,
    output: '0 1 1 2 3 5 8 13 21 34',
  },
  'C++': {
    code: `#include <iostream>
using namespace std;
int main() {
    int a = 0, b = 1;
    for (int i = 0; i < 10; i++) {
        cout << a << " ";
        int t = a + b; a = b; b = t;
    }
}`,
    output: '0 1 1 2 3 5 8 13 21 34',
  },
  JavaScript: {
    code: `function fibonacci(n) {
  let a = 0, b = 1;
  const r = [];
  for (let i = 0; i < n; i++) {
    r.push(a);
    [a, b] = [b, a + b];
  }
  console.log(r.join(" "));
}
fibonacci(10);`,
    output: '0 1 1 2 3 5 8 13 21 34',
  },
  Go: {
    code: `package main
import "fmt"
func main() {
    a, b := 0, 1
    for i := 0; i < 10; i++ {
        fmt.Printf("%d ", a)
        a, b = b, a+b
    }
}`,
    output: '0 1 1 2 3 5 8 13 21 34',
  },
  Rust: {
    code: `fn main() {
    let (mut a, mut b) = (0u64, 1u64);
    for _ in 0..10 {
        print!("{} ", a);
        let t = a + b; a = b; b = t;
    }
}`,
    output: '0 1 1 2 3 5 8 13 21 34',
  },
  C: {
    code: `#include <stdio.h>
int main() {
    int a = 0, b = 1;
    for (int i = 0; i < 10; i++) {
        printf("%d ", a);
        int t = a + b; a = b; b = t;
    }
}`,
    output: '0 1 1 2 3 5 8 13 21 34',
  },
};

// Mini code editor component
const MiniEditor: React.FC<{
  lang: string; code: string; filename: string; output?: string;
  showOutput?: boolean; onRun?: () => void; isRunning?: boolean;
}> = ({ lang, code, filename, output, showOutput, onRun, isRunning }) => (
  <div className="rounded-lg border border-white/10 bg-[#12121a] overflow-hidden flex-1 min-w-0">
    <div className="flex items-center justify-between px-3 py-1.5 bg-white/[0.03] border-b border-white/5">
      <div className="flex items-center gap-2">
        <div className="flex gap-1">
          <div className="w-2 h-2 rounded-full bg-red-500/50" />
          <div className="w-2 h-2 rounded-full bg-yellow-500/50" />
          <div className="w-2 h-2 rounded-full bg-green-500/50" />
        </div>
        <span className="text-[10px] text-gray-500 ml-1">{filename}</span>
      </div>
      {onRun && (
        <button
          onClick={onRun}
          disabled={isRunning}
          className={`px-2.5 py-0.5 text-[10px] font-semibold rounded transition-all ${
            isRunning
              ? 'bg-purple-600/30 text-purple-300 cursor-wait'
              : 'bg-purple-600 hover:bg-purple-500 text-white'
          }`}
        >
          {isRunning ? 'Running...' : 'Run'}
        </button>
      )}
    </div>
    <div className="p-3 font-mono text-[11px] leading-5 text-gray-400 overflow-x-auto max-h-[180px]">
      <pre className="whitespace-pre">{code}</pre>
    </div>
    {showOutput && output && (
      <div className="border-t border-white/5 bg-black/20 px-3 py-2">
        <span className="font-mono text-[11px] text-green-400">{output}</span>
      </div>
    )}
  </div>
);

const RunCodeOnlinePage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedLang, setSelectedLang] = useState('Python');
  const [selectedPair, setSelectedPair] = useState(DUAL_PAIRS[0]);
  const [isRunning, setIsRunning] = useState(false);
  const [showOutput, setShowOutput] = useState(false);
  const [typedOutput, setTypedOutput] = useState('');
  const [dualLeftRunning, setDualLeftRunning] = useState(false);
  const [dualLeftOutput, setDualLeftOutput] = useState(false);
  const [dualRightRunning, setDualRightRunning] = useState(false);
  const [dualRightOutput, setDualRightOutput] = useState(false);

  // Track ad visit on page load — capture UTM params from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasUtm = params.get('utm_source') || params.get('gclid');
    if (hasUtm) {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      fetch(`${apiUrl}/api/ad-visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          landing_page: '/run-code-online',
          utm_source: params.get('utm_source') || undefined,
          utm_medium: params.get('utm_medium') || undefined,
          utm_campaign: params.get('utm_campaign') || undefined,
          utm_content: params.get('utm_content') || undefined,
          utm_term: params.get('utm_term') || undefined,
          gclid: params.get('gclid') || undefined,
          referrer: document.referrer || undefined,
        }),
      })
        .then(r => r.json())
        .then(data => {
          if (data.visit_id) {
            localStorage.setItem('ad_visit_id', String(data.visit_id));
          }
        })
        .catch(() => {});
    }
  }, []);

  const demo = DEMO_CODE[selectedLang];

  useEffect(() => {
    setShowOutput(false);
    setTypedOutput('');
  }, [selectedLang]);

  // Reset dual outputs when pair changes
  useEffect(() => {
    setDualLeftOutput(false);
    setDualRightOutput(false);
  }, [selectedPair]);

  const handleDualRun = (side: 'left' | 'right') => {
    const setter = side === 'left' ? setDualLeftRunning : setDualRightRunning;
    const outputSetter = side === 'left' ? setDualLeftOutput : setDualRightOutput;
    setter(true);
    outputSetter(false);
    setTimeout(() => {
      setter(false);
      outputSetter(true);
    }, 800);
  };

  const handleRun = () => {
    setIsRunning(true);
    setShowOutput(false);
    setTypedOutput('');
    setTimeout(() => {
      setIsRunning(false);
      setShowOutput(true);
      const output = demo.output;
      let idx = 0;
      const interval = setInterval(() => {
        setTypedOutput(output.slice(0, idx + 1));
        idx++;
        if (idx >= output.length) clearInterval(interval);
      }, 20);
    }, 800);
  };

  const ctaClick = (position: string) => {
    // Fire GA4 event
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'cta_click', {
        event_category: 'conversion',
        event_label: `run_code_online_${position}`,
        page: '/run-code-online',
        cta_position: position,
      });
    }
    navigate(`/login?utm_source=run-code-online&utm_medium=cta&utm_campaign=run_code_now&utm_content=${position}`);
  };

  const ctaButton = (position: string) => (
    <button
      onClick={() => ctaClick(position)}
      className="px-7 py-2.5 text-sm font-semibold bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 rounded-xl transition-all hover:shadow-xl hover:shadow-purple-500/20 hover:scale-105"
    >
      Run Code Now — No Setup Required
    </button>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <header className="border-b border-white/5 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
          <div className="w-7 h-7 bg-purple-600 rounded-lg flex items-center justify-center font-bold text-xs">C</div>
          <span className="font-semibold">Cubemate</span>
        </div>
        <button
          onClick={() => ctaClick('header')}
          className="px-4 py-1.5 text-sm font-semibold bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 rounded-lg transition-all"
        >
          Run Code Now
        </button>
      </header>

      <main className="max-w-5xl mx-auto px-6 pt-8 pb-10">

        {/* ── Section 1: Hero + CTA (above fold) ── */}
        <div className="text-center mb-5">
          <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-2">
            Run Code Instantly <span className="text-purple-400">in Your Browser</span>
          </h1>
          <p className="text-base text-gray-400 mb-5">
            No setup. No IDE. Python, Java, C++, Go, Rust, JavaScript, and C.
          </p>
          {ctaButton('hero')}
          <p className="mt-2 text-xs text-gray-500">One-click start with Google/email. No credit card needed.</p>
        </div>

        {/* ── Section 2: Single Language Editor ── */}
        <div className="flex justify-center gap-1.5 mb-3 flex-wrap">
          {LANGUAGES.map(lang => (
            <button
              key={lang.name}
              onClick={() => setSelectedLang(lang.name)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                selectedLang === lang.name
                  ? 'bg-purple-600/20 text-purple-300 border border-purple-500/40'
                  : 'text-gray-500 hover:text-white hover:bg-white/5 border border-transparent'
              }`}
            >
              {lang.name}
            </button>
          ))}
        </div>

        <div className="rounded-xl border border-white/10 bg-[#12121a] overflow-hidden shadow-2xl shadow-purple-500/5">
          <div className="flex items-center justify-between px-4 py-2 bg-white/[0.03] border-b border-white/5">
            <div className="flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
              </div>
              <span className="text-xs text-gray-500 ml-2">main.{LANGUAGES.find(l => l.name === selectedLang)?.icon}</span>
            </div>
            <button
              onClick={handleRun}
              disabled={isRunning}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                isRunning
                  ? 'bg-purple-600/30 text-purple-300 cursor-wait'
                  : 'bg-purple-600 hover:bg-purple-500 text-white'
              }`}
            >
              {isRunning ? 'Running...' : 'Run'}
            </button>
          </div>
          <div className="p-4 font-mono text-sm leading-6 text-gray-300 overflow-x-auto">
            <pre className="whitespace-pre">{demo.code}</pre>
          </div>
          {(showOutput || isRunning) && (
            <div className="border-t border-white/5 bg-black/20">
              <div className="px-4 py-1 text-[10px] uppercase tracking-widest text-gray-500 border-b border-white/5">Output</div>
              <div className="px-4 py-3 font-mono text-sm text-green-400">
                {isRunning ? <span className="text-gray-500 animate-pulse">Executing...</span> : typedOutput}
              </div>
            </div>
          )}
        </div>

        {/* ── Section 3: Dual Language Compare & Run ── */}
        <div className="mt-14">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold mb-2">
              Compare & Run <span className="text-purple-400">Two Languages</span> Side by Side
            </h2>
            <p className="text-sm text-gray-400">
              Learn faster by seeing the same program in two languages. Run both independently.
            </p>
          </div>

          {/* Dual pair tabs */}
          <div className="flex justify-center gap-2 mb-4 flex-wrap">
            {DUAL_PAIRS.map(pair => (
              <button
                key={pair.label}
                onClick={() => setSelectedPair(pair)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  selectedPair.label === pair.label
                    ? 'bg-purple-600/20 text-purple-300 border border-purple-500/40'
                    : 'text-gray-500 hover:text-white hover:bg-white/5 border border-transparent'
                }`}
              >
                {pair.label}
              </button>
            ))}
          </div>

          {/* Side-by-side mockup */}
          <div className="flex gap-3">
            <MiniEditor
              lang={selectedPair.left}
              code={DEMO_CODE[selectedPair.left].code}
              filename={`main.${LANGUAGES.find(l => l.name === selectedPair.left)?.icon}`}
              output={DEMO_CODE[selectedPair.left].output}
              showOutput={dualLeftOutput}
              onRun={() => handleDualRun('left')}
              isRunning={dualLeftRunning}
            />
            <MiniEditor
              lang={selectedPair.right}
              code={DEMO_CODE[selectedPair.right].code}
              filename={`main.${LANGUAGES.find(l => l.name === selectedPair.right)?.icon}`}
              output={DEMO_CODE[selectedPair.right].output}
              showOutput={dualRightOutput}
              onRun={() => handleDualRun('right')}
              isRunning={dualRightRunning}
            />
          </div>

          {/* CTA after dual section */}
          <div className="text-center mt-8">
            {ctaButton('dual_compare')}
            <p className="mt-2 text-xs text-gray-500">One-click start with Google/email. 100 free credits.</p>
          </div>
        </div>

        {/* ── Section 4: Workflow — Charts & Data ── */}
        <div className="mt-16 pt-12 border-t border-white/5">
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 rounded-full mb-4">
              Workflow
            </span>
            <h2 className="text-2xl font-bold mb-2">
              From prompt to{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                dataset + chart
              </span>
            </h2>
            <p className="text-sm text-gray-400 max-w-md mx-auto">
              One prompt to search, download data, create CSVs, and generate publication-ready visualizations.
            </p>
          </div>

          {/* Prompt example */}
          <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5 mb-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-cyan-400" />
              <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">Prompt</span>
            </div>
            <p className="text-sm text-gray-200 leading-relaxed italic">
              "Download the Iris dataset and create a scatter plot of sepal_length vs petal_length colored by species"
            </p>
          </div>

          {/* Result preview */}
          <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10">
              <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">Result</span>
            </div>
            <div className="p-5 space-y-3">
              {/* CSV download */}
              <div className="flex items-center gap-3 p-3 bg-white/[0.03] rounded-lg border border-white/5">
                <div className="w-8 h-8 rounded-lg bg-green-500/15 flex items-center justify-center text-green-400 text-sm">
                  &#128196;
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">iris_dataset.csv</p>
                  <p className="text-xs text-gray-500">150 rows &middot; 5 columns</p>
                </div>
                <span className="px-3 py-1 text-xs font-semibold text-green-400 bg-green-500/10 rounded-lg border border-green-500/20">
                  Download
                </span>
              </div>

              {/* Chart preview */}
              <div className="rounded-lg overflow-hidden border border-white/10">
                <img
                  src="/workflow-example.png"
                  alt="Iris scatter plot generated by Cubemate Workflow"
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-8">
            {ctaButton('workflow')}
            <p className="mt-2 text-xs text-gray-500">Search, scrape, download, visualize — all in one prompt.</p>
          </div>
        </div>

        {/* ── Section 5: Stats ── */}
        <div className="grid grid-cols-3 gap-6 mt-14 text-center">
          <div>
            <div className="text-2xl font-bold mb-1">7</div>
            <div className="text-sm text-gray-400">Languages</div>
          </div>
          <div>
            <div className="text-2xl font-bold mb-1">&lt;1s</div>
            <div className="text-sm text-gray-400">Execution</div>
          </div>
          <div>
            <div className="text-2xl font-bold mb-1">Free</div>
            <div className="text-sm text-gray-400">100 credits</div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 px-6 py-5 text-center text-xs text-gray-500">
        Cubemate AI &mdash; Powered by{' '}
        <a href="https://corestratai.com" target="_blank" rel="noopener noreferrer" className="hover:underline text-gray-400">corestratai.com</a>
      </footer>
    </div>
  );
};

export default RunCodeOnlinePage;
